import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { useAppDispatch, useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { pages } from '@/settings/pages';
import { adminsApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

import { PasswordEditForm } from '../Presentation';
import { Loading } from '@/components/utils/Loading';
import { useCheckReAuthenticationExpiration } from '@/hooks/useCheckReAuthenticationExpiration';

export const PasswordEditFormContainer: React.FC = () => {
  const { apiErrorHandler } = useApiErrorHandler();
  const dispatch = useAppDispatch();
  const { flashNotice, flashError } = useFlashMessenger();
  const admin = useAppSelector(authStore.selectors.meSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMismatchError, setHasMismatchError] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const checkReAuthenticationExpiration = useCheckReAuthenticationExpiration(
    setIsLoading,
    pages.account.password.edit,
  );

  useEffect(() => {
    checkReAuthenticationExpiration();
  }, [checkReAuthenticationExpiration]);

  const onSubmit = useCallback(
    async (currentPassword: string, newPassword: string) => {
      try {
        setIsLoading(true);
        const session = await adminsApi.updatePassword({
          data: {
            currentPassword,
            newPassword,
            adminId: admin.id,
          },
        });
        dispatch(authStore.actions.setSession(session));
        flashNotice(t('text.account.password.edit.done'));
        navigate(pages.account.index);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response !== undefined) {
          if (err.response && err.response.status === 400) {
            setHasMismatchError(true);
          } else if (
            err.code !== 'ECONNABORTED' &&
            err.response &&
            err.response.status === undefined
          ) {
            // timeout以外の原因でresponseがない場合
            flashError(t('ui.error.network_error'));
          } else {
            apiErrorHandler(err);
          }
          setIsLoading(false);
        }
      }
    },
    [admin.id, apiErrorHandler, dispatch, flashError, flashNotice, navigate, t],
  );

  const childProps = { isLoading, hasMismatchError, onSubmit };
  return isLoading ? <Loading /> : <PasswordEditForm {...childProps} />;
};
